import React, { useState, useEffect } from 'react';
import { ChannelFormData, RoutingConfig, Gateway, GatewayChannel } from '../types';
import { apiClient } from '../../../api';
import { parseFabricUrl } from '../../../utils/channelUtils';

interface McpProxy {
  id: string;
  name: string;
  description: string;
  base_url: string;
  endpoint_path: string;
  status: 'active' | 'disabled';
}

interface McpProxySelectorProps {
  formData: ChannelFormData;
  setFormData: React.Dispatch<React.SetStateAction<ChannelFormData>>;
  setIsModified: (value: boolean) => void;
}

const McpProxySelector: React.FC<McpProxySelectorProps> = ({ formData, setFormData, setIsModified }) => {
  const [mcpProxies, setMcpProxies] = useState<McpProxy[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadProxies = async () => {
      try {
        setLoading(true);
        const proxies = await apiClient.get('/mcp-proxies');
        setMcpProxies(proxies.data || []);
      } catch (error) {
        console.error('Failed to load MCP proxies:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProxies();
  }, []);

  const selectedProxy = mcpProxies.find(p => p.id === formData.mcp_proxy_id);

  return (
    <>
      <select
        className="form-control mb-2 dropdown-styling"
        id="channel-edit-mcp-proxy"
        value={formData.mcp_proxy_id || ''}
        onChange={(e) => {
          const proxyId = e.target.value;
          setIsModified(true);
          setFormData(prev => ({
            ...prev,
            mcp_proxy_id: proxyId,
            // Use proxy:// scheme for target_endpoint
            target_endpoint: proxyId ? `proxy://${proxyId}` : '',
          }));
        }}
        disabled={loading}
      >
        <option value="">
          {loading ? '-- Loading proxies... --' : '-- Select an MCP Proxy --'}
        </option>
        {mcpProxies
          .filter(p => p.status === 'active')
          .map(proxy => (
            <option key={proxy.id} value={proxy.id}>{proxy.name}</option>
          ))}
      </select>
      <small className="form-text text-muted">
        Select an MCP proxy to expose through this channel
      </small>

      {selectedProxy && (
        <div className="alert alert-info mt-3">
          <strong>Proxy Details:</strong>
          <div className="mt-2">
            <small>
              <strong>Base URL:</strong> {selectedProxy.base_url}<br />
              <strong>Endpoint Path:</strong> {selectedProxy.endpoint_path}<br />
              <strong>Description:</strong> {selectedProxy.description}
            </small>
          </div>
        </div>
      )}
    </>
  );
};

export interface RoutingTabProps {
  formData: ChannelFormData;
  handleInputChange: (field: keyof ChannelFormData, value: string | boolean) => void;
  setFormData: React.Dispatch<React.SetStateAction<ChannelFormData>>;
  setIsModified: (value: boolean) => void;
  setValidationErrors: React.Dispatch<React.SetStateAction<string[]>>;
  routingConfig: RoutingConfig | null;
  selectedHostPort: string;
  setSelectedHostPort: (value: string) => void;
  selectedPrefix: string;
  setSelectedPrefix: (value: string) => void;
  customPath: string;
  setCustomPath: (value: string) => void;
  isCreateMode: boolean;
  gateways: Gateway[];
  gatewayChannels: GatewayChannel[];
  isLoadingChannels: boolean;
  setGatewayChannels: React.Dispatch<React.SetStateAction<GatewayChannel[]>>;
  loadedGatewayRef: React.MutableRefObject<string | undefined>;
  refreshGatewayChannelsCache: () => Promise<void>;
  isRefreshingChannels: boolean;
  fullRouteDisplay: string;
  channelNameCache: Map<string, string>;
  channel?: {
    fabric_target_name?: string;
  };
}

const RoutingTab: React.FC<RoutingTabProps> = ({
  formData,
  handleInputChange,
  setFormData,
  setIsModified,
  setValidationErrors,
  routingConfig,
  selectedHostPort,
  setSelectedHostPort,
  selectedPrefix,
  setSelectedPrefix,
  customPath,
  setCustomPath,
  isCreateMode,
  gateways,
  gatewayChannels,
  isLoadingChannels,
  setGatewayChannels,
  loadedGatewayRef,
  refreshGatewayChannelsCache,
  isRefreshingChannels,
  fullRouteDisplay,
  channelNameCache,
  channel,
}) => {
  const getFabricDisplayNames = (endpoint: string): { gatewayName: string; channelName: string } => {
    const parsed = parseFabricUrl(endpoint);
    if (!parsed || !parsed.channelId) {
      return { gatewayName: '', channelName: '' };
    }

    const gateway = gateways.find(g => g.id === parsed.gatewayId);
    const gatewayName = gateway?.name || parsed.gatewayId || 'Unknown Gateway';
    
    // First check if we have a stored fabric_target_name in the channel data
    if (channel?.fabric_target_name) {
      return { gatewayName, channelName: channel.fabric_target_name };
    }
    
    // Then check cache
    if (channelNameCache.has(parsed.channelId)) {
      return { gatewayName, channelName: channelNameCache.get(parsed.channelId)! };
    }
    
    // Then try gatewayChannels
    const channelInList = gatewayChannels.find(c => c.config_id === parsed.channelId);
    if (channelInList) {
      return { gatewayName, channelName: channelInList.name };
    }
    
    return { gatewayName, channelName: '' };
  };

  return (
    <>
      {/* Full Route Info */}
      <div className="alert alert-success mb-3" style={{ fontSize: '14px' }}>
        <i className="fas fa-info-circle mr-2"></i><strong>Channel Route:</strong> {fullRouteDisplay}
        <button
          type="button"
          className="btn btn-xs ml-2"
          style={{ padding: '2px 6px', fontSize: '11px', outline: 'none' }}
          onClick={(e) => {
            navigator.clipboard.writeText(fullRouteDisplay);
            const btn = e.currentTarget as HTMLButtonElement;
            const originalHtml = btn.innerHTML;
            btn.innerHTML = '<i class="fas fa-check"></i>';
            setTimeout(() => { btn.innerHTML = originalHtml; }, 2000);
          }}
          title="Copy to clipboard"
        >
          <i className="fas fa-copy"></i>
        </button>
      </div>

      {/* Channel Mapping Info */}
      <div className="alert alert-info mb-3" style={{ fontSize: '14px' }}>
        <div className="mb-2">
          <i className="fas fa-route mr-2"></i>
          <strong>Target: </strong> {formData.target_endpoint.startsWith('fabric://') ? (
            (() => {
              const { gatewayName, channelName } = getFabricDisplayNames(formData.target_endpoint);
              const parsed = parseFabricUrl(formData.target_endpoint);
              return (
                <span title={formData.target_endpoint}>                              
                    {gatewayName}
                    {channelName && <> → {channelName}</>}
                    {!channelName && parsed?.channelId && (
                      <span className="text-muted"> → {parsed.channelId.substring(0, 8)}...</span>
                    )}                            
                </span>
              );
            })()
          ) : (
            <span>{formData.target_endpoint}</span>
          )}
          <button
            type="button"
            className="btn btn-xs ml-2"
            style={{ padding: '2px 6px', fontSize: '11px', outline: 'none' }}
            onClick={(e) => {
              navigator.clipboard.writeText(formData.target_endpoint);
              const btn = e.currentTarget as HTMLButtonElement;
              const originalHtml = btn.innerHTML;
              btn.innerHTML = '<i class="fas fa-check"></i>';
              setTimeout(() => { btn.innerHTML = originalHtml; }, 2000);
            }}
            title="Copy to clipboard"
          >
            <i className="fas fa-copy"></i>
          </button>
        </div>
      </div>


      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-route"></i> Listener
            </h6>
          </div>
        </div>
        <div className="card-body">

      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="channel-edit-host-port">Listen Address (host : port) <span className="text-danger">*</span></label>
          <select
            className="form-control dropdown-styling"
            id="channel-edit-host-port"
            value={selectedHostPort}
            onChange={(e) => {
              setSelectedHostPort(e.target.value);
              setIsModified(true);
            }}
            disabled={!routingConfig || routingConfig.available_listen_addresses.length === 0}
          >
            {!routingConfig ? (
              <option value="">Loading...</option>
            ) : routingConfig.available_listen_addresses.length === 0 ? (
              <option value="">No addresses available</option>
            ) : (
              <>
                {isCreateMode && <option value="">-- Select listen address --</option>}
                {routingConfig.available_listen_addresses.map((addr) => (
                  <option key={addr} value={addr}>
                    {addr}
                  </option>
                ))}
              </>
            )}
          </select>
          <small className="form-text text-muted">Select the host:port this server will listen on</small>
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="channel-edit-prefix">Channel Prefix <span className="text-danger">*</span></label>
          <select
            className="form-control dropdown-styling"
            id="channel-edit-prefix"
            value={selectedPrefix}
            onChange={(e) => {
              setSelectedPrefix(e.target.value);
              setIsModified(true);
            }}
            disabled={!routingConfig || routingConfig.channel_path_prefix.length === 0}
          >
            {!routingConfig ? (
              <option value="">Loading...</option>
            ) : routingConfig.channel_path_prefix.length === 0 ? (
              <option value="">No prefixes available</option>
            ) : (
              <>
                {isCreateMode && <option value="">-- Select prefix --</option>}
                {routingConfig.channel_path_prefix.map((item) => (
                  <option key={item.id} value={item.prefix}>
                    {item.name}
                  </option>
                ))}
              </>
            )}
          </select>
          <small className="form-text text-muted">Select the path prefix for your channel</small>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12 mb-3">
          <label htmlFor="channel-edit-custom-path">Custom Path <span className="text-danger">*</span></label>
          <input 
            type="text" 
            className="form-control" 
            id="channel-edit-custom-path"
            value={customPath.startsWith('/') ? customPath.substring(1) : customPath}
            onChange={(e) => {
              setCustomPath(e.target.value);
              setIsModified(true);
            }}
            placeholder="my/custom/path"
          />
          <small className="form-text text-muted">Your custom path (will be prefixed with {selectedPrefix || 'prefix'}/)</small>
        </div>
      </div></div></div>

            <div className="card shadow-sm mb-4">
        <div className="card-header bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-bullseye"></i> Target
            </h6>
          </div>
        </div>
        <div className="card-body">

      
      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="channel-edit-endpoint-type">Target Endpoint</label>
          <select 
            className="form-control mb-4 dropdown-styling" 
            id="channel-edit-endpoint-type"
            value={formData.endpoint_type || 'url'}
            onChange={(e) => {
              const newType = e.target.value as 'url' | 'gateway' | 'mcp-proxy';
              setIsModified(true);
              setValidationErrors([]); // Clear validation errors when switching types
              setFormData(prev => ({
                ...prev,
                endpoint_type: newType,
                target_endpoint: newType === 'url' ? prev.target_endpoint : '',
                gateway_id: newType === 'gateway' ? prev.gateway_id : undefined,
                gateway_channel: newType === 'gateway' ? prev.gateway_channel : undefined,
                mcp_proxy_id: newType === 'mcp-proxy' ? prev.mcp_proxy_id : undefined,
              }));
            }}
          >
            <option value="url">Direct URL</option>
            <option value="gateway">via Gateway Connection</option>
            {formData.protocol === 'mcp' && (
              <option value="mcp-proxy">via MCP Proxy</option>
            )}
          </select>
          
          {formData.endpoint_type === 'url' ? (
            <>
              <input 
                type="text" 
                className="form-control" 
                id="channel-edit-target"
                value={formData.target_endpoint}
                onChange={(e) => handleInputChange('target_endpoint', e.target.value)}
                placeholder="https://example.com/a2a/v1"
              />
              <small className="form-text text-muted">Enter the full URL including protocol (http:// or https://)</small>
            </>
          ) : formData.endpoint_type === 'mcp-proxy' ? (
            <McpProxySelector
              formData={formData}
              setFormData={setFormData}
              setIsModified={setIsModified}
            />
          ) : (
            <>
              <select 
                className="form-control mb-2 dropdown-styling" 
                id="channel-edit-gateway"
                value={formData.gateway_id || ''}
                onChange={async (e) => {
                  const gatewayId = e.target.value;
                  setIsModified(true);
                  
                  // Reset loaded gateway ref to allow reloading when user changes gateway
                  loadedGatewayRef.current = undefined;
                  
                  // Clear current channels immediately for better UX
                  setGatewayChannels([]);
                  
                  setFormData(prev => ({
                    ...prev,
                    gateway_id: gatewayId,
                    gateway_channel: undefined,  // Reset channel selection
                  }));
                  
                  // The useEffect will handle loading channels for the new gateway
                }}
              >
                <option value="">-- Select a Gateway --</option>
                {gateways.map(gateway => (
                  <option key={gateway.id} value={gateway.id}>{gateway.name}</option>
                ))}
              </select>
              <small className="form-text text-muted mb-2">Select the gateway connection</small>
              
              {formData.gateway_id && (
                <>
                  <select 
                    className="form-control dropdown-styling" 
                    id="channel-edit-gateway-channel"
                    value={formData.gateway_channel || ''}
                    onChange={(e) => {
                      const channelId = e.target.value;
                      setIsModified(true);
                      setFormData(prev => ({
                        ...prev,
                        gateway_channel: channelId,
                        // Build the target_endpoint using fabric:// format with gateway ID and channel ID
                        target_endpoint: `fabric://${prev.gateway_id}/${channelId}`,
                      }));
                    }}
                    disabled={isLoadingChannels || gatewayChannels.length === 0}
                  >
                    <option value="">
                      {isLoadingChannels ? '-- Loading channels... --' : '-- Select a Channel --'}
                    </option>
                    {gatewayChannels.map(channel => (
                      <option key={channel.config_id} value={channel.config_id}>{channel.name}</option>
                    ))}
                  </select>
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <small className="form-text text-muted mb-0">Select the channel on the gateway</small>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={refreshGatewayChannelsCache}
                      disabled={isRefreshingChannels}
                      title="Refresh all gateway channels from connected gateways"
                    >
                      <i className={`fas fa-sync-alt ${isRefreshingChannels ? 'fa-spin' : ''}`}></i>
                      {isRefreshingChannels ? ' Refreshing...' : ' Refresh Channels'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      </div></div>
    </>
  );
};

export default RoutingTab;
